import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import BrandMark from "../ui/BrandMark.jsx";
import DocsLink from "../ui/DocsLink.jsx";

const linkStyle = ({ isActive }) => ({
  display: "block",
  padding: "7px 10px",
  borderRadius: "var(--radius)",
  fontSize: "var(--text-sm)",
  textDecoration: "none",
  color: isActive ? "var(--text-accent)" : "var(--text-primary)",
  background: isActive ? "var(--bg-accent)" : "transparent",
});

/**
 * The same shape as a nav item, for the one entry that leaves the app. It is an
 * anchor rather than a `NavLink` because the API reference is a static site
 * outside the SPA's routes (see ui/DocsLink.jsx).
 */
const externalLinkStyle = {
  display: "block",
  padding: "7px 10px",
  borderRadius: "var(--radius)",
  fontSize: "var(--text-sm)",
  textDecoration: "none",
  color: "var(--text-primary)",
};

export default function AppShell() {
  const { user, signOut } = useAuth();
  // Closed by default: on a phone the navigation would otherwise cover the
  // content on every load, and on a desktop the query never shows the control.
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="ical-shell">
      <aside
        className="ical-nav"
        data-open={navOpen}
        // Any tap inside the drawer is a navigation, and on a phone the drawer
        // that stays open after one is covering the page it just opened.
        onClick={() => setNavOpen(false)}
        style={{
          width: 208,
          flexShrink: 0,
          borderRight: "1px solid var(--border)",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/*
          The brand sits at the top of the nav: mark, then name. The mark is
          wider than it is tall, so it is sized by height and takes its width
          from the viewBox; the colour comes from the accent token so it follows
          the light and dark themes.
        */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "0 0 14px",
          }}
        >
          <BrandMark
            style={{
              height: 26,
              width: "auto",
              flexShrink: 0,
              color: "var(--text-accent)",
            }}
          />
          <span style={{ fontWeight: 500 }}>YourCals</span>
        </div>

        <NavLink to="/" end style={linkStyle}>
          Dashboard
        </NavLink>
        <NavLink to="/billing" style={linkStyle}>
          Billing
        </NavLink>
        <NavLink to="/notifications" style={linkStyle}>
          Notifications
        </NavLink>
        <NavLink to="/account" style={linkStyle}>
          Account
        </NavLink>
        <NavLink to="/upgrade" style={linkStyle}>
          Plan &amp; limits
        </NavLink>
        {user?.can_admin && (
          <NavLink to="/admin" style={linkStyle}>
            Admin
          </NavLink>
        )}

        {/*
          The customer-facing /v1 reference. It belongs in the main navigation
          rather than buried in a settings screen: the panel's job is to hand the
          customer a working integration, and the reference is how they build one.

          Help sits beside it because both answer "I am stuck": one with the
          reference that explains what the API expects, one with a form that
          reaches a person when the reference does not.
        */}
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
          <DocsLink style={externalLinkStyle}>API docs</DocsLink>
          <NavLink to="/help" style={linkStyle}>
            Help
          </NavLink>
        </div>

        <div style={{ marginTop: "auto", paddingTop: 16, borderTop: "1px solid var(--border)" }}>
          <p
            style={{
              margin: "0 0 8px",
              fontSize: "var(--text-xs)",
              color: "var(--text-secondary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={user?.email}
          >
            {user?.email}
          </p>
          {/*
            The verification banner sits in the shell rather than on one screen:
            an unverified account is blocked from creating projects and adding
            calendars wherever it tries, and the fix is one link away.
          */}
          {user && user.email_verified === false ? (
            <NavLink
              to="/account"
              style={{
                display: "block",
                marginBottom: 8,
                padding: "6px 8px",
                borderRadius: "var(--radius)",
                background: "var(--bg-warning)",
                color: "var(--text-warning)",
                fontSize: "var(--text-xs)",
                textDecoration: "none",
              }}
            >
              Verify your email to create projects
            </NavLink>
          ) : null}
          <button
            type="button"
            className="ical-btn"
            style={{ width: "100%", padding: "6px 10px", fontSize: "var(--text-xs)" }}
            onClick={handleSignOut}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Closes the drawer by tapping what it covers. */}
      <div className="ical-nav-scrim" data-open={navOpen} onClick={() => setNavOpen(false)} />

      <main className="ical-shell-main">
        {/*
          The menu control sits at the top left on a narrow screen and nowhere at
          all on a wide one, where the navigation is already beside the content
          and a button to reveal it would be a control that does nothing.
        */}
        <div className="ical-shell-topbar">
          <button
            type="button"
            className="ical-nav-toggle"
            aria-label="Menu"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((open) => !open)}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d="M2 4.5h14M2 9h14M2 13.5h14" />
              </g>
            </svg>
          </button>
        </div>

        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
